"use client";

import React from "react";
import { ArrowRight, CircleCheck as CheckCircle2, FileText, Layers, ShieldCheck, Sparkles } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
const instrumentFamilies = [{
  name: "Rigaku ResQ Series",
  model: "ResQ, ResQ QC & ResQ CQL 1064nm",
  capability: "Narcotics, explosives, and chemical warfare identification with non-contact sampling.",
  status: "Validated accessories available"
}, {
  name: "Rigaku CQL Family",
  model: "CQL Gen-ID & CQL Max-ID",
  capability: "Tactical Raman analysis for hazardous materials, threat screening, and field triage.",
  status: "Field-ready mounting kits"
}, {
  name: "QuickDetect Solutions",
  model: "QuickDetect Surface & Trace Tools",
  capability: "Trace detection, vial compartments, tablet holders, and specialized optical nose cones.",
  status: "Sampling tools in stock"
}];
export default function SafetyHero() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 15
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="safetyaccessorieshero" data-nav="light" className="relative w-full bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="page-container">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          {/* Left Column: 7 cols */}
          <motion.div className="lg:col-span-7 flex flex-col items-start gap-6" initial="hidden" animate="visible" variants={containerVariants}>
            <Badge variant="secondary" className="px-3 py-1 text-xs font-semibold gap-1.5 border border-border">
              <ShieldCheck className="size-3.5 text-primary" />
              Safety &amp; Security Product Line
            </Badge>

            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground leading-[1.15]">
              Accessories and sampling tools for ResQ, CQL, and QuickDetect analyzers
            </h1>

            <div className="space-y-4 text-base md:text-lg text-muted-foreground leading-relaxed max-w-2xl">
              <p>
                Equip your field inspection, defense, and laboratory response teams with specialized sampling adapters,
                protective holsters, replacement nose cones, and calibration standards designed specifically for Rigaku
                1064 nm Raman analyzers.
              </p>
              <p>
                From non-contact vial holders and solid-surface test fixtures to ruggedized transit cases and tactical
                charging cradles, Antech Solutions supplies verified hardware to maximize your handheld instrument
                uptime and detection accuracy in demanding operational environments.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-4 pt-2">
              <Button asChild size="lg" className="shadow-sm">
                <Link to="/DiscussYourApplicationPage" className="inline-flex items-center gap-2">
                  Request Accessories Catalog
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link to="/BookADemoPage" className="inline-flex items-center gap-2">
                  <FileText className="size-4" />
                  Book a Demonstration
                </Link>
              </Button>
            </div>

            <div className="pt-4 border-t border-border w-full flex flex-wrap items-center gap-6 text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1.5">
                <CheckCircle2 className="size-4 text-primary" />
                Ireland &amp; UK Direct Support
              </span>
              <span className="inline-flex items-center gap-1.5">
                <CheckCircle2 className="size-4 text-primary" />
                OEM Certified Components
              </span>
              <span className="inline-flex items-center gap-1.5">
                <CheckCircle2 className="size-4 text-primary" />
                Immediate Dispatch on Core Items
              </span>
            </div>
          </motion.div>

          {/* Right Column: 5 cols Showcase */}
          <motion.div className="lg:col-span-5 w-full" initial="hidden" animate="visible" variants={containerVariants} transition={{
          delay: 0.15
        }}>
            <Card className="border border-border bg-card shadow-md">
              <CardHeader className="pb-4 border-b border-border">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 text-primary font-medium text-xs tracking-wider uppercase">
                    <Layers className="size-4" />
                    Compatible Systems
                  </div>
                  <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-md font-mono">
                    Rigaku / QuickDetect
                  </span>
                </div>
                <CardTitle className="text-xl font-bold text-card-foreground mt-1">
                  Supported Instrument Families
                </CardTitle>
                <CardDescription className="text-muted-foreground text-sm">
                  Full accessory suites engineered for seamless optical and mechanical alignment.
                </CardDescription>
              </CardHeader>

              <CardContent className="pt-6 space-y-4">
                {instrumentFamilies.map((family, i) => <div key={family.name} data-index={i} className="p-4 rounded-lg bg-secondary/50 border border-border/70 hover:border-border transition-colors flex flex-col gap-1.5">
                    <div className="flex items-center justify-between gap-2">
                      <h2 className="font-semibold text-foreground text-sm flex items-center gap-1.5">
                        <Sparkles className="size-3.5 text-primary shrink-0" />
                        {family.name}
                      </h2>
                      <span className="text-[11px] font-medium text-primary bg-primary/10 px-2 py-0.5 rounded-full">
                        {family.status}
                      </span>
                    </div>
                    <p className="text-xs font-mono text-muted-foreground">
                      {family.model}
                    </p>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      {family.capability}
                    </p>
                  </div>)}

                <div className="pt-2">
                  <Button asChild variant="ghost" size="sm" className="w-full justify-between text-xs font-semibold">
                    <Link to="/RamanHandheldAnalyzersPage" className="inline-flex items-center justify-between w-full">
                      <span>Explore all Raman handheld analyzers</span>
                      <ArrowRight className="size-3.5" />
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>;
}